import React, { useEffect, useMemo } from "react";
import { Box, Container } from "@mui/material";
import { useAppContext } from "../context/AppContext";
import { useStories } from "../hooks/useStories";
import { useTrendingStories } from "../hooks/useTrendingStories";
import { usePopularStories } from "../hooks/usePopularStories";
import { useTopStoriesByCategories } from "../hooks/useTopStoriesByCategories";
import { useHomepageImagePreload } from "../hooks/useImagePreload";
import HomePageShimmer from "../components/UI/Shimmers/HomePageShimmer";
import { HomeCarouselSection } from "../components/Home";
import WhatsNewBanner from "../components/Home/WhatsNewBanner";
import ContestBanner from "../components/ContestBanner";
import { FEATURED_CATEGORIES, formatCategoryName } from "../utility/category.utils";
import { gql, useQuery } from "@apollo/client";
import type { StoryCategory } from "../types/models";

const GET_FEATURED_INK_MASTER = gql`
  query GetFeaturedInkMaster {
    featuredInkMaster {
      current {
        id
        title
        banner
        period {
          start
          end
        }
        rewards {
          position
          amount
        }
      }
      upcoming {
        id
        title
        banner
        period {
          start
          end
        }
        rewards {
          position
          amount
        }
      }
      completed {
        id
        title
        banner
        period {
          start
          end
        }
        rewards {
          position
          amount
        }
      }
    }
  }
`;

const HomePage = () => {
  const { setActive } = useAppContext();

  // Fetch trending stories (for "Top picks for you" section) - HIGHEST PRIORITY (LCP)
  const {
    stories: trendingStories = [],
    isLoading: loadingTrendingStories,
  } = useTrendingStories(12); // Only fetch 12 for carousel display

  // Fetch popular stories (for "Top Stories" section) - MEDIUM PRIORITY
  const {
    stories: popularStories = [],
    isLoading: loadingPopularStories,
  } = usePopularStories(12); // Only fetch 12 for carousel display

  // Fetch latest stories (for "New Releases" section) - MEDIUM PRIORITY
  const { data: latestStories = [], isLoading: loadingLatestStories } = useStories({
    latestOnly: true,
    limit: 12, // Only fetch 12 for carousel display
  });

  // Fetch featured Ink Master for banner - LOW PRIORITY (defer until after initial render)
  const { data: bannerData, loading: loadingFeaturedQuest } = useQuery(
    GET_FEATURED_INK_MASTER,
    {
      // Defer this query until after initial render
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    }
  );

  // Get category configs from FEATURED_CATEGORIES for titles and priority
  const categoriesToFetch = useMemo(() => {
    const targetCategories: StoryCategory[] = [
      "FanFiction",
      "ScienceFiction",
      "Lifestyle",
      "MentalHealth",
      "PersonalGrowth",
      "Romance",
    ];
    return FEATURED_CATEGORIES.filter((config) =>
      targetCategories.includes(config.category)
    ).sort((a, b) => a.priority - b.priority);
  }, []);

  // Fetch all category stories in a single optimized query (replaces 6 separate queries)
  const categoryStoriesList = useMemo(
    () => categoriesToFetch.map((config) => config.category),
    [categoriesToFetch]
  );
  
  const {
    categoryStoriesMap,
    isLoading: loadingCategoryStories,
  } = useTopStoriesByCategories(categoryStoriesList, 12);

  useEffect(() => {
    setActive("home");
  }, [setActive]);

  // Preload first 3 images from trending stories for better LCP
  useHomepageImagePreload(trendingStories);

  // Check if initial sections are loading (trending, popular, latest)
  const initialSectionsLoading =
    loadingTrendingStories || loadingPopularStories || loadingLatestStories;

  // Prepare Ink Masters banner data with status from backend
  const inkMastersQuests = useMemo(() => {
    if (!bannerData?.featuredInkMaster) return [];
    
    const quests = [];
    
    // Add current (ongoing) quests with status
    if (bannerData.featuredInkMaster.current) {
      quests.push({ ...bannerData.featuredInkMaster.current, status: "ongoing" });
    }
    
    // Add upcoming quests with status
    if (bannerData.featuredInkMaster.upcoming) {
      quests.push({ ...bannerData.featuredInkMaster.upcoming, status: "upcoming" });
    }
    
    // Add completed quests with status
    if (bannerData.featuredInkMaster.completed) {
      quests.push({ ...bannerData.featuredInkMaster.completed, status: "completed" });
    }
    
    return quests;
  }, [bannerData]);

  // Show shimmer only while initial sections are loading
  // Category sections will render progressively as they load
  if (initialSectionsLoading) {
    return <HomePageShimmer />;
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 4, sm: 6, md: 8 },
        px: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      {/* What's New Banner */}
      <WhatsNewBanner />

      {/* Ink Masters Contest Banner - Reserve space to prevent layout shift */}
      <Box 
        sx={{ 
          mb: inkMastersQuests.length > 0 ? { xs: 4, sm: 5, md: 6 } : 0,
          minHeight: inkMastersQuests.length > 0 ? "auto" : 0,
          visibility: inkMastersQuests.length > 0 ? "visible" : "hidden",
          opacity: inkMastersQuests.length > 0 ? 1 : 0,
          transition: "opacity 0.2s ease",
        }}
      >
        {inkMastersQuests.length > 0 && <ContestBanner quests={inkMastersQuests} />}
      </Box>

      <Box>
        {/* Top picks for you - Renders immediately when data loads */}
        {trendingStories.length > 0 && (
          <HomeCarouselSection
            title="Top picks for you"
            stories={trendingStories}
          />
        )}

        {/* Hot/Popular Stories Section - Renders immediately when data loads */}
        {popularStories.length > 0 && (
          <HomeCarouselSection
            title="Top Stories"
            stories={popularStories}
            showNumbers={true}
          />
        )}

        {/* New Releases / Latest Stories Section - Renders immediately when data loads */}
        {latestStories.length > 0 && (
          <HomeCarouselSection title="New Releases" stories={latestStories} />
        )}

        {/* Category-Based Sections: "Top in [Category]" - Progressive loading with reserved space */}
        {categoriesToFetch.map((config) => {
          const stories = categoryStoriesMap.get(config.category) || [];
          const isLoading = loadingCategoryStories;
          const hasStories = stories.length > 0;
          
          // Always render container to reserve space and prevent layout shift
          // Reserve space matching the actual carousel section height
          return (
            <Box
              key={config.category}
              sx={{
                minHeight: isLoading || !hasStories ? "280px" : "auto",
                mb: { xs: 4, sm: 5, md: 8 },
              }}
            >
              {hasStories && (
                <Box
                  sx={{
                    visibility: isLoading ? "hidden" : "visible",
                    opacity: isLoading ? 0 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <HomeCarouselSection
                    title={config.title || `Top in ${formatCategoryName(config.category)}`}
                    stories={stories}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Container>
  );
};

export default HomePage;
